import React, { Suspense, lazy, useContext, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import { DataProvider, DataContext } from './contexts/DataContext';
import { Bell, XCircle } from 'lucide-react';
import { isSellerScopedUser, normalizeSellerKey } from './utils/sellerIdentity';
import './index.css';

const Inventory = lazy(() => import('./pages/Inventory'));
const POS = lazy(() => import('./pages/POS'));
const History = lazy(() => import('./pages/History'));
const FranchiseHistory = lazy(() => import('./pages/FranchiseHistory'));
const Closing = lazy(() => import('./pages/Closing'));
const Sefaz = lazy(() => import('./pages/Sefaz'));
const Settings = lazy(() => import('./pages/Settings'));
const OnlineOrders = lazy(() => import('./pages/OnlineOrders'));
const OperatorArea = lazy(() => import('./pages/OperatorArea'));
const DigitalCatalog = lazy(() => import('./pages/DigitalCatalog'));
const ClientProfile = lazy(() => import('./pages/ClientProfile'));
const ClientLogin = lazy(() => import('./pages/ClientLogin'));
const SystemLogin = lazy(() => import('./pages/SystemLogin'));
const FastOrder = lazy(() => import('./pages/FastOrder'));
const Chat = lazy(() => import('./pages/Chat'));
const Factory = lazy(() => import('./pages/Factory'));
const Clients = lazy(() => import('./pages/Clients'));
const Franchisees = lazy(() => import('./pages/Franchisees'));
const Financial = lazy(() => import('./pages/Financial'));
const Charges = lazy(() => import('./pages/Charges'));
const Interior = lazy(() => import('./pages/Interior'));
const Suppliers = lazy(() => import('./pages/Suppliers'));
const StockControl = lazy(() => import('./pages/StockControl'));
const JuniorReports = lazy(() => import('./pages/JuniorReports'));
const FranchiseReports = lazy(() => import('./pages/FranchiseReports'));
const SellersDashboard = lazy(() => import('./pages/SellersDashboard'));
const Expedition = lazy(() => import('./pages/Expedition'));
const ManagementDashboard = lazy(() => import('./pages/ManagementDashboard'));
const VendasDetalhadas = lazy(() => import('./pages/VendasDetalhadas'));
const MySales = lazy(() => import('./pages/MySales'));
const MyCommission = lazy(() => import('./pages/MyCommission'));
const MyDelinquents = lazy(() => import('./pages/MyDelinquents'));

const PageLoading = () => (
    <div style={{ padding: '24px', color: '#6B7076', fontWeight: 700 }}>
        Carregando...
    </div>
);

const RequireAuth = ({ children }) => {
    const { systemUser } = useContext(DataContext);
    const location = useLocation();
    if (!systemUser) {
        return <Navigate to="/login" replace />;
    }
    if (systemUser.role === 'operator' || systemUser.role === 'general_manager' || systemUser.role === 'op_varejo') {
        const pathname = String(location.pathname || '').trim().toLowerCase();
        const allowedDirectPages = [
            '/fechamento',
            '/vendas',
            '/operador',
            '/historico',
            '/clientes',
            '/estoque'
        ];
        if (pathname === '/operador') return children;
        if (allowedDirectPages.some((p) => pathname === p || pathname.startsWith(p + '/'))) {
            return children;
        }
        return <Navigate to="/vendas" replace />;
    }
    if (systemUser.role === 'factory') {
        return <Navigate to="/fabrica" replace />;
    }
    return children;
};

const RequireOperator = ({ children }) => {
    const { systemUser } = useContext(DataContext);
    if (!systemUser) {
        return <Navigate to="/login" replace />;
    }
    if (systemUser.role !== 'operator') {
        return <Navigate to="/pedidos" replace />;
    }
    return children;
};

const RequireExpedition = ({ children }) => {
    const { systemUser } = useContext(DataContext);
    if (!systemUser) {
        return <Navigate to="/login" replace />;
    }
    if (systemUser.role !== 'expedition' && systemUser.role !== 'admin') {
        return <Navigate to="/pedidos" replace />;
    }
    return children;
};

const RequireFactory = ({ children }) => {
    const { systemUser } = useContext(DataContext);
    if (!systemUser) {
        return <Navigate to="/login" replace />;
    }
    if (systemUser.role !== 'factory' && systemUser.role !== 'admin') {
        return <Navigate to="/pedidos" replace />;
    }
    return children;
};

const RequireJunior = ({ children }) => {
    const { systemUser } = useContext(DataContext);
    if (!systemUser) {
        return <Navigate to="/login" replace />;
    }
    const username = String(systemUser?.username || '').trim().toLowerCase();
    if (username !== 'junior') {
        return <Navigate to="/pedidos" replace />;
    }
    return children;
};

const RequireDaniloOrJunior = ({ children }) => {
    const { systemUser } = useContext(DataContext);
    if (!systemUser) {
        return <Navigate to="/login" replace />;
    }
    if (!isDaniloOrJuniorUser(systemUser)) {
        return <Navigate to="/pedidos" replace />;
    }
    return children;
};

const isJuniorManagementUser = (systemUser) => String(systemUser?.username || '').trim().toLowerCase() === 'junior';
const isDaniloOrJuniorUser = (systemUser) => {
    const username = String(systemUser?.username || '').trim().toLowerCase();
    const name = String(systemUser?.name || '').trim().toLowerCase();
    const role = String(systemUser?.role || '').trim().toLowerCase();
    return username === 'junior' || username === 'danilo' || name === 'danilo' || role === 'supervisor';
};
const isRayaneFranchiseUser = (systemUser) => {
    const username = String(systemUser?.username || '').trim().toLowerCase();
    const name = String(systemUser?.name || '').trim().toLowerCase();
    return username === 'rayane' || username === 'rayane_master' || name === 'rayane';
};
const isDuduHybridUser = (systemUser) => {
    const username = String(systemUser?.username || '').trim().toLowerCase();
    const name = String(systemUser?.name || '').trim().toLowerCase();
    return username === 'dudu' || name === 'dudu' || name === 'eduardo';
};
const isDuduFranchiseUser = (systemUser) => {
    const role = String(systemUser?.role || '').trim().toLowerCase();
    return role === 'franchise_manager' || role === 'franchise_analyst';
};
const isFranchiseReportUser = (systemUser) => {
    const role = String(systemUser?.role || '').trim().toLowerCase();
    return role === 'admin' || isRayaneFranchiseUser(systemUser) || isDuduHybridUser(systemUser);
};

const RequireFranchiseReport = ({ children }) => {
    const { systemUser } = useContext(DataContext);
    if (!systemUser) {
        return <Navigate to="/login" replace />;
    }
    if (!isFranchiseReportUser(systemUser)) {
        return <Navigate to="/pedidos" replace />;
    }
    return children;
};

const isJuniorAllowedPath = (pathname = '') => {
    const path = String(pathname || '').trim().toLowerCase();
    return ['/gestao', '/pedidos', '/recebimentos', '/financeiro', '/cobrancas', '/cobranças', '/vendedores', '/minhas-vendas', '/historico', '/relatorios-junior', '/vendas-detalhadas', '/config'].includes(path);
};

const isSellerFinancialUser = (systemUser, settings) => {
    if (!systemUser) return false;
    if (normalizeSellerKey(systemUser?.role) === 'admin') return false;
    if (normalizeSellerKey(systemUser?.role) === 'supervisor') return false;
    const isDaniloOrJunior = isDaniloOrJuniorUser(systemUser);
    if (isDaniloOrJunior) return false;
    return isSellerScopedUser(systemUser, settings?.sales_sellers, {
        excludedRoles: ['admin', 'supervisor']
    });
};

const isSellerStockRestrictedUser = (systemUser, settings) => {
    if (!systemUser) return false;
    const role = normalizeSellerKey(systemUser?.role);
    if (role === 'admin' || role === 'supervisor') return false;
    if (isDaniloOrJuniorUser(systemUser)) return false;
    if (isRayaneFranchiseUser(systemUser) || isDuduHybridUser(systemUser)) return false;
    if (role === 'store') return true;
    return isSellerScopedUser(systemUser, settings?.sales_sellers, {
        excludedRoles: ['admin', 'supervisor']
    });
};

const isPedroWebRestrictedUser = (systemUser) => {
    const username = normalizeSellerKey(systemUser?.username || '');
    const name = normalizeSellerKey(systemUser?.name || '');
    if (username === 'pedro' || name === 'pedro') return true;
    if (username.split(/[\s._-]+/)[0] === 'pedro') return true;
    if (name.split(/[\s._-]+/)[0] === 'pedro') return true;
    return false;
};

const SystemShell = () => {
    const { onlineOrders, history, inventory, systemUser, settings, ensureRouteData } = useContext(DataContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [, setNotificationHistory] = useState([]);
    const [, setShowNotificationPanel] = useState(false);
    const prevOrdersRef = useRef([]);
    const prevSalesRef = useRef([]);
    const prevLowStockRef = useRef(new Set());
    const prevExpiryRef = useRef(new Set());
    const isFirstRunRef = useRef(true);

    const [isScrolled, setIsScrolled] = useState(false);

    const isJunior = isJuniorManagementUser(systemUser);
    const isPedroRestricted = isPedroWebRestrictedUser(systemUser);
    const isRayaneRestricted = isRayaneFranchiseUser(systemUser);
    const isDuduRestricted = isDuduFranchiseUser(systemUser);
    const shouldUseSellerFinancial = isSellerFinancialUser(systemUser, settings);
    const isSellerStockRestricted = isSellerStockRestrictedUser(systemUser, settings);

    // Helper for sound
    const playNotificationSound = () => {
        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (AudioContext) {
                const ctx = new AudioContext();
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.type = 'sine';
                osc.frequency.setValueAtTime(880, ctx.currentTime);
                osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.1);
                gain.gain.setValueAtTime(0.1, ctx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
                osc.start();
                osc.stop(ctx.currentTime + 0.1);
            }
        } catch (e) {
            console.error("Audio play failed", e);
        }
    };

    useEffect(() => {
        // Request Notification Permission
        if ('Notification' in window && Notification.permission !== 'granted') {
            Notification.requestPermission();
        }

        // Also listen to window scroll for robustness on mobile
        const handleWindowScroll = () => {
            if (window.scrollY > 10) setIsScrolled(true);
            else if (!document.querySelector('.main-content')?.scrollTop) setIsScrolled(false);
        };
        window.addEventListener('scroll', handleWindowScroll);
        return () => window.removeEventListener('scroll', handleWindowScroll);
    }, []);

    useEffect(() => {
        if (!systemUser) return;
        ensureRouteData(location.pathname);
    }, [ensureRouteData, location.pathname, systemUser]);

    const handleScroll = (e) => {
        setIsScrolled(e.target.scrollTop > 10);
    };

    const removeNotification = (id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    };

    const handleNotificationClick = (notification) => {
        // Mark as read
        setNotificationHistory(prev => prev.map(n => 
            n.id === notification.id ? { ...n, read: true } : n
        ));
        
        if (notification.type === 'order' && notification.orderId) {
            navigate('/pedidos', { state: { focusOrderId: notification.orderId } });
            setShowNotificationPanel(false);
        }
    };

    useEffect(() => {
        const orderIds = onlineOrders
            .filter(o => ['pending', 'approval', 'production', 'separating', 'ready_for_pickup', 'delivering'].includes(String(o.status || '').toLowerCase()))
            .map(o => o.id)
            .filter(id => id != null);

        const sales = history.filter(h => h.saleDetails || (typeof h.type === 'string' && h.type.toLowerCase().includes('venda')));
        const saleIds = sales.map(s => s.id).filter(id => id != null);

        const getStockState = (p) => {
            const qr = Number(p.quantityRetail || 0);
            const qw = Number(p.quantityWholesale || 0);
            const minStock = Number(p.minStock ?? p.stockMin ?? 0);
            const threshold = (Number.isFinite(minStock) && minStock > 0) ? minStock : 3;
            const isZero = (Number.isFinite(qr) && qr <= 0) || (Number.isFinite(qw) && qw <= 0);
            if (isZero) return 'zero';
            const isLow = (qr > 0 && qr <= threshold) || (qw > 0 && qw <= threshold);
            if (isLow) return 'low';
            return null;
        };
        const lowStockItems = inventory
            .map(p => ({ p, state: getStockState(p) }))
            .filter(x => x.state);
        const lowStockKeys = lowStockItems.map(x => `${x.p.id ?? x.p._id ?? x.p.name}|${x.state}`);
        const lowStockSet = new Set(lowStockKeys);

        const parseValidityDate = (v) => {
            const s = String(v || '').trim();
            if (!s) return null;
            const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            const d = new Date(s);
            if (!Number.isNaN(d.getTime())) return new Date(d.getFullYear(), d.getMonth(), d.getDate());
            return null;
        };
        const today = new Date();
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const expiringSoonDays = 7;
        const expiryItems = inventory
            .map(p => {
                const vd = parseValidityDate(p.validityDate);
                if (!vd) return null;
                const diffDays = Math.floor((vd.getTime() - todayStart.getTime()) / (24 * 60 * 60 * 1000));
                if (diffDays < 0) return { p, state: 'expired', diffDays };
                if (diffDays <= expiringSoonDays) return { p, state: 'expiring', diffDays };
                return null;
            })
            .filter(Boolean);
        const expiryKeys = expiryItems.map(x => `${x.p.id ?? x.p._id ?? x.p.name}|${x.state}`);
        const expirySet = new Set(expiryKeys);

        if (isFirstRunRef.current) {
            // Initialize sales and stock refs immediately to prevent notification blasts on refresh
            prevSalesRef.current = saleIds;
            prevLowStockRef.current = lowStockSet;
            prevExpiryRef.current = expirySet;

            // Try to load seen orders from localStorage to avoid re-notifying (or missing new ones)
            try {
                const seenOrders = JSON.parse(localStorage.getItem('doutor_seen_orders') || '[]');
                if (seenOrders.length > 0) {
                    // If we have history, use it. This allows detecting NEW orders that arrived while closed.
                    prevOrdersRef.current = seenOrders;
                    // Don't return here! Let the logic below run to find differences.
                } else {
                    // First time ever on this device? Mark all current as seen to avoid spam.
                    prevOrdersRef.current = orderIds;
                    // Save them immediately
                    localStorage.setItem('doutor_seen_orders', JSON.stringify(orderIds));
                    isFirstRunRef.current = false;
                    return; 
                }
            } catch (e) {
                // Fallback
                prevOrdersRef.current = orderIds;
                isFirstRunRef.current = false;
                return;
            }
            
            isFirstRunRef.current = false;
            // If we loaded seenOrders, we continue execution to check for diffs
        }

        const newNotifications = [];

        const newOrderIds = orderIds.filter(id => !prevOrdersRef.current.includes(id));
        if (newOrderIds.length > 0) {
            newOrderIds.forEach(id => {
                const order = onlineOrders.find(o => o.id === id);
                newNotifications.push({
                    id: Date.now() + Math.random(),
                    type: 'order',
                    orderId: id,
                    message: `Novo pedido #${id.toString().slice(-6)}`,
                    clientName: order?.client?.name || 'Cliente',
                    itemCount: order?.items?.length || 0,
                    total: order?.total || 0,
                    timestamp: Date.now(),
                    read: false
                });
            });
            // Update seen orders in localStorage
            const updatedSeen = [...prevOrdersRef.current, ...newOrderIds];
            // Limit seen orders to last 500 to prevent infinite growth
            const truncatedSeen = updatedSeen.slice(-500);
            localStorage.setItem('doutor_seen_orders', JSON.stringify(truncatedSeen));
            prevOrdersRef.current = truncatedSeen;
        } else {
            // Even if no new orders, we should update prevOrdersRef to current state if needed? 
            // Actually, if we just keep adding seen IDs, we are good.
            // But we must ensure prevOrdersRef tracks what we've processed.
            // If we didn't add anything, prevOrdersRef is fine. 
            // BUT, if orders were removed (completed/cancelled), they might disappear from orderIds.
            // We should probably keep them in "seen" so if they reappear we don't notify? 
            // Or just keep accumulating "seen" IDs.
        }

        const newSaleIds = saleIds.filter(id => !prevSalesRef.current.includes(id));
        if (newSaleIds.length > 0) {
                newNotifications.push({
                    id: Date.now() + Math.random(),
                    message: `${newSaleIds.length} nova(s) venda(s) registrada(s)`,
                    type: 'sale',
                    timestamp: Date.now(),
                    read: false
            });
        }

        const prevLowSet = prevLowStockRef.current;
        const newLowKeys = lowStockKeys.filter(key => !prevLowSet.has(key));
        if (newLowKeys.length > 0) {
            const firstKey = newLowKeys[0];
            const [firstId] = String(firstKey).split('|');
            const first = lowStockItems.find(x => String(x.p.id ?? x.p._id ?? x.p.name) === String(firstId));
            if (first?.p) {
                const label = first.state === 'zero' ? 'Estoque zerado' : 'Estoque baixo';
                newNotifications.push({
                    id: Date.now() + Math.random(),
                    message: `${label}: ${first.p.name}`,
                    type: 'stock',
                    timestamp: Date.now(),
                    read: false
                });
            }
        }

        const prevExpirySet = prevExpiryRef.current;
        const newExpiryKeys = expiryKeys.filter(key => !prevExpirySet.has(key));
        if (newExpiryKeys.length > 0) {
            const firstKey = newExpiryKeys[0];
            const [firstId] = String(firstKey).split('|');
            const first = expiryItems.find(x => String(x.p.id ?? x.p._id ?? x.p.name) === String(firstId));
            if (first?.p) {
                const label = first.state === 'expired' ? 'Produto vencido' : 'Produto vencendo';
                newNotifications.push({
                    id: Date.now() + Math.random(),
                    message: `${label}: ${first.p.name}`,
                    type: 'expiry',
                    timestamp: Date.now(),
                    read: false
                });
            }
        }

        if (newNotifications.length > 0) {
            setNotifications(prev => [...prev, ...newNotifications]);
            setNotificationHistory(prev => [...newNotifications, ...prev]);
            
            playNotificationSound();

            // Trigger System Notification
            if ('Notification' in window && Notification.permission === 'granted') {
                newNotifications.forEach(n => {
                    try {
                        const notif = new Notification(n.message, {
                            body: n.type === 'order' 
                                ? `${n.clientName} • ${n.itemCount} itens • R$ ${Number(n.total).toFixed(2)}`
                                : (n.details || n.message),
                            icon: '/vite.svg',
                            tag: n.type === 'order' ? `order-${n.orderId}` : undefined,
                            vibrate: [200, 100, 200]
                        });
                        
                        notif.onclick = function() {
                            window.focus();
                            handleNotificationClick(n);
                        };
                    } catch (e) {
                        console.error("System notification error:", e);
                    }
                });
            }
            
            const idsToRemove = newNotifications.map(n => n.id);
            setTimeout(() => {
                setNotifications(prev => prev.filter(n => !idsToRemove.includes(n.id)));
            }, 5000);
        }

        prevOrdersRef.current = orderIds;
        prevSalesRef.current = saleIds;
        prevLowStockRef.current = lowStockSet;
        prevExpiryRef.current = expirySet;
    }, [onlineOrders, history, inventory]);

    if (isJunior && !isJuniorAllowedPath(location.pathname)) {
        return <Navigate to="/gestao" replace />;
    }

    if (isPedroRestricted && ['/sefaz', '/config'].includes(String(location.pathname || '').trim().toLowerCase())) {
        return <Navigate to="/pedidos" replace />;
    }

    if (
        isRayaneRestricted &&
        ['/gestao', '/historico', '/clientes', '/interior', '/vendedores', '/fechamento', '/config', '/recebimentos', '/financeiro', '/cobrancas', '/cobranças'].includes(String(location.pathname || '').trim().toLowerCase())
    ) {
        return <Navigate to="/relatorios-franquia" replace />;
    }

    if (
        isDuduRestricted &&
        ['/gestao', '/historico', '/clientes', '/relatorios-franquia', '/interior', '/vendedores', '/fechamento', '/config', '/sefaz', '/recebimentos', '/financeiro', '/cobrancas', '/cobranças', '/repasse', '/franquias', '/fornecedores', '/estoque-controle'].includes(String(location.pathname || '').trim().toLowerCase())
    ) {
        return <Navigate to="/historico-franquia" replace />;
    }

    if (isSellerStockRestricted && String(location.pathname || '').trim().toLowerCase() === '/estoque') {
        return <Navigate to="/pedidos" replace />;
    }

    if (
        shouldUseSellerFinancial &&
        ['/recebimentos', '/financeiro'].includes(String(location.pathname || '').trim().toLowerCase())
    ) {
        return <Navigate to="/financeiro-vendedor" replace />;
    }
    if (
        shouldUseSellerFinancial &&
        ['/cobrancas', '/cobranças'].includes(String(location.pathname || '').trim().toLowerCase())
    ) {
        return <Navigate to="/cobrancas-vendedor" replace />;
    }

    return (
        <div className="app-container">
            <Sidebar isScrolled={isScrolled} />
            
            <div style={{ display: 'none' }} className="global-notification-bell-removed"></div>

                <div className="main-content" onScroll={handleScroll}>

                    {/* In-App Notifications disabled as per user request (keeping only Chrome Push) */}
                    <div style={{
                        position: 'fixed',
                        top: '70px',
                        right: '20px',
                        zIndex: 9999,
                        display: 'none', // Hidden
                        flexDirection: 'column',
                        gap: '10px',
                        pointerEvents: 'none'
                    }}>
                        {notifications.map(notification => (
                            <div
                                key={notification.id}
                            style={{
                                background: notification.type === 'sale'
                                    ? '#16a34a'
                                    : notification.type === 'stock'
                                    ? '#f97316'
                                    : notification.type === 'expiry'
                                    ? '#ef4444'
                                    : '#3b82f6',
                                color: 'white',
                                padding: '1rem 1.5rem',
                                borderRadius: '12px',
                                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                animation: 'slideIn 0.3s ease-out',
                                pointerEvents: 'auto',
                                minWidth: '300px'
                            }}
                        >
                            <div style={{ background: 'rgba(255,255,255,0.2)', padding: '8px', borderRadius: '50%' }}>
                                <Bell size={20} fill="white" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 'bold' }}>Notificação</h4>
                                <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.9 }}>{notification.message}</p>
                            </div>
                            <button
                                onClick={() => removeNotification(notification.id)}
                                style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', padding: '4px' }}
                            >
                                <XCircle size={18} />
                            </button>
                        </div>
                    ))}
                </div>
                <Suspense fallback={<PageLoading />}>
                <Routes>
                    <Route path="/" element={<Navigate to={isRayaneFranchiseUser(systemUser) ? "/relatorios-franquia" : (isDuduFranchiseUser(systemUser) ? "/historico-franquia" : ((systemUser?.role === 'admin' || isJunior) ? "/gestao" : "/pedidos"))} replace />} />
                    <Route path="/gestao" element={isRayaneFranchiseUser(systemUser) ? <Navigate to="/relatorios-franquia" replace /> : (isDuduFranchiseUser(systemUser) ? <Navigate to="/historico-franquia" replace /> : <ManagementDashboard />)} />
                    <Route path="/estoque" element={<Inventory />} />
                    <Route path="/vendas" element={<POS />} />
                    <Route path="/fechamento" element={<Closing />} />
                    <Route path="/historico" element={<History />} />
                    <Route path="/historico-franquia" element={<FranchiseHistory />} />
                    <Route path="/relatorios-junior" element={
                        <RequireJunior>
                            <JuniorReports />
                        </RequireJunior>
                    } />
                    <Route path="/vendas-detalhadas" element={
                        <RequireDaniloOrJunior>
                            <VendasDetalhadas />
                        </RequireDaniloOrJunior>
                    } />
                    <Route path="/relatorios-franquia" element={
                        <RequireFranchiseReport>
                            <FranchiseReports />
                        </RequireFranchiseReport>
                    } />
                    <Route path="/pedidos" element={<OnlineOrders />} />
                    <Route path="/vendedores" element={<SellersDashboard />} />
                    <Route path="/expedicao" element={<Expedition />} />
                    <Route path="/sefaz" element={<Sefaz />} />
                    <Route path="/config" element={<Settings />} />
                    <Route path="/fast-order" element={<FastOrder />} />
                    <Route path="/chat" element={<Chat />} />
                    <Route path="/recebimentos" element={<Financial />} />
                    <Route path="/financeiro" element={<Navigate to="/recebimentos" replace />} />
                    <Route path="/financeiro-vendedor" element={<Financial view="seller" />} />
                    <Route path="/minhas-vendas" element={<MySales />} />
                    <Route path="/minha-comissao" element={<MyCommission />} />
                    <Route path="/cobrancas" element={<Charges />} />
                    <Route path="/cobrancas-vendedor" element={<Charges view="seller" />} />
                    <Route path="/meus-inadimplentes" element={<MyDelinquents />} />
                    <Route path="/repasse" element={<Financial view="repasse" />} />
                    <Route path="/franquias" element={<Financial view="franchises" />} />
                    <Route path="/interior" element={<Interior />} />
                    <Route path="/clientes" element={<Clients />} />
                    <Route path="/franqueados" element={<Franchisees />} />
                    <Route path="/fornecedores" element={<Suppliers />} />
                    <Route path="/estoque-controle" element={<StockControl />} />
                    <Route path="*" element={<Navigate to={isRayaneFranchiseUser(systemUser) ? "/relatorios-franquia" : (isDuduFranchiseUser(systemUser) ? "/historico-franquia" : (isJunior ? "/gestao" : "/pedidos"))} replace />} />
                </Routes>
                </Suspense>
            </div>
        </div>
    );
};

function App() {
  return (
    <DataProvider>
      <Router>
        <Suspense fallback={<PageLoading />}>
        <Routes>
          {/* Rotas Públicas */}
          <Route path="/login" element={<SystemLogin />} />
          <Route path="/catalogo/login" element={<ClientLogin />} />
          <Route path="/catalogo" element={<DigitalCatalog />} />
          <Route path="/perfil" element={<Navigate to="/perfil/cadastro" replace />} />
          <Route path="/perfil/cadastro" element={<ClientProfile view="cadastro" />} />
          <Route path="/perfil/pedidos" element={<ClientProfile view="pedidos" />} />
          <Route path="/operador" element={
            <RequireOperator>
              <OperatorArea />
            </RequireOperator>
          } />

          <Route path="/expedicao-area" element={
            <RequireExpedition>
              <FastOrder />
            </RequireExpedition>
          } />
          <Route path="/fabrica" element={
            <RequireFactory>
              <Factory />
            </RequireFactory>
          } />
          
          {/* Rotas do Sistema Administrativo - Protegidas */}
          <Route path="/*" element={
            <RequireAuth>
                <SystemShell />
            </RequireAuth>
          } />
        </Routes>
        </Suspense>
      </Router>
    </DataProvider>
  );
}

export default App;
